import React from 'react';
/**
 * PracticeSession full-page states — Loading, Error, Empty variants.
 *
 * Extracted from PracticeSession.tsx in s69 to reduce its size from 978→~680 lines.
 * All components are pure presentational (no state management).
 */
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { DjayAvatar, getDjayThinkingMessage } from '../../mascot';
// ── Loading Spinner (shared animation) ───────────────────────
function LoadingSpinner() {
  return <motion.div animate={{
    scale: [1, 1.05, 1],
    rotate: [0, 3, -3, 0]
  }} transition={{
    duration: 2.5,
    repeat: Infinity,
    ease: 'easeInOut'
  }} className="mx-auto mb-6" data-id="element-6947">
      {/* @djay-slot: practice-loading */}
      <DjayAvatar size="xl" emotion="thinking" data-id="element-6948" />
    </motion.div>;
}
// ── Loading State ────────────────────────────────────────────
export function SessionLoadingState({
  isAssessmentMode
}: {
  isAssessmentMode: boolean;
}) {
  return <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAFAF8]" data-id="element-6949">
      <div className="text-center" data-id="element-6950">
        <LoadingSpinner data-id="element-6951" />
        <h3 className="text-2xl font-black text-slate-800 mb-3" data-id="element-6952">
          Even geduld...
        </h3>
        <p className="text-slate-500 font-medium text-lg" data-id="element-6953">
          {isAssessmentMode ? 'We maken je toets klaar! ✨' : 'We zoeken de beste vragen voor jou! ✨'}
        </p>
      </div>
    </div>;
}
// ── Error State (generation failed) ──────────────────────────
export function SessionErrorState({
  isAssessmentMode,
  onExit
}: {
  isAssessmentMode: boolean;
  onExit: () => void;
}) {
  return <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center p-6" data-id="element-6954">
      <div className="max-w-lg w-full p-12 text-center bg-white rounded-[2.5rem] shadow-2xl shadow-rose-100 border-2 border-rose-100" data-id="element-6955">
        <div className="text-6xl mb-6" data-id="element-6956">😕</div>
        <h2 className="text-3xl font-black text-slate-900 mb-4 tracking-tight" data-id="element-6957">
          Er ging iets mis
        </h2>
        <p className="text-slate-600 mb-8 text-lg leading-relaxed font-medium" data-id="element-6958">
          {isAssessmentMode ? 'De toets kon niet worden geladen.' : 'De oefensessie kon niet worden geladen.'}
        </p>
        <Button onClick={onExit} variant="primary" size="xxl" fullWidth className="shadow-2xl shadow-indigo-200" data-id="element-6959">
          Terug naar overzicht
        </Button>
      </div>
    </div>;
}
// ── Empty: No Content Available ──────────────────────────────
export function SessionEmptyNoContent({
  onExit
}: {
  onExit: () => void;
}) {
  return <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center p-6" data-id="element-6960">
      <div className="max-w-lg w-full p-8 sm:p-12 text-center bg-white rounded-[2.5rem] shadow-2xl shadow-indigo-100 border-2 border-indigo-100" data-id="element-6961">
        <div className="flex justify-center mb-6" data-id="element-6962">
          <DjayAvatar size="xl" emotion="thinking" data-id="element-6963" />
        </div>
        <h2 className="text-3xl sm:text-4xl font-black text-slate-900 mb-4 tracking-tight" data-id="element-6964">
          Je moet eerst meer oefenen!
        </h2>
        <p className="text-slate-600 mb-8 text-lg leading-relaxed font-medium" data-id="element-6965">
          Er zijn op dit moment nog geen vragen beschikbaar voor dit onderwerp.
          Probeer eerst wat andere onderwerpen te oefenen!
        </p>
        <Button onClick={onExit} variant="primary" size="xxl" fullWidth className="shadow-xl shadow-indigo-200" data-id="element-6966">
          Kies een ander vak
        </Button>
      </div>
    </div>;
}
// ── Empty: All Mastered ──────────────────────────────────────
export function SessionEmptyAllMastered({
  selectedGroup,
  onExit
}: {
  selectedGroup: number | string;
  onExit: () => void;
}) {
  return <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center p-6" data-id="element-6967">
      <div className="max-w-lg w-full p-12 text-center bg-white rounded-[2.5rem] shadow-2xl shadow-indigo-100 border-2 border-indigo-100" data-id="element-6968">
        <motion.div animate={{
        scale: [1, 1.1, 1],
        rotate: [0, 5, -5, 0]
      }} transition={{
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="text-8xl mb-6 drop-shadow-lg" data-id="element-6969">
          🌟
        </motion.div>
        <h2 className="text-4xl font-black text-slate-900 mb-4 tracking-tight" data-id="element-6970">
          Geweldig gedaan!
        </h2>
        <p className="text-slate-600 mb-8 text-xl leading-relaxed font-medium" data-id="element-6971">
          Je hebt alle vragen voor dit vak in groep {selectedGroup} super goed
          geoefend! 🎉
        </p>
        <p className="text-amber-600 font-bold text-lg mb-10" data-id="element-6972">
          Probeer een ander vak of kom later terug voor meer uitdaging!
        </p>
        <Button onClick={onExit} variant="primary" size="xxl" fullWidth className="shadow-2xl shadow-indigo-200" data-id="element-6973">
          <Sparkles className="w-6 h-6 mr-2 fill-current" data-id="element-6974" />
          Terug naar overzicht
        </Button>
      </div>
    </div>;
}
// ── Empty: Fallback (no progress, shouldn't happen) ──────────
export function SessionEmptyFallback({
  isAssessmentMode,
  onExit
}: {
  isAssessmentMode: boolean;
  onExit: () => void;
}) {
  return <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center p-6" data-id="element-6975">
      <div className="max-w-lg w-full p-12 text-center bg-white rounded-[2.5rem] shadow-2xl shadow-rose-100 border-2 border-rose-100" data-id="element-6976">
        <div className="text-6xl mb-6" data-id="element-6977">🤔</div>
        <h2 className="text-3xl font-black text-slate-900 mb-4 tracking-tight" data-id="element-6978">
          Er ging iets mis
        </h2>
        <p className="text-slate-600 mb-8 text-lg leading-relaxed font-medium" data-id="element-6979">
          {isAssessmentMode ? 'De toets kon niet worden gemaakt.' : 'We konden geen oefensessie maken.'}{' '}
          Probeer het nog eens!
        </p>
        <Button onClick={onExit} variant="primary" size="xxl" fullWidth className="shadow-2xl shadow-indigo-200" data-id="element-6980">
          Terug naar overzicht
        </Button>
      </div>
    </div>;
}
// ── Safety Error (question undefined but session has questions) ─
export function SessionSafetyError({
  onExit
}: {
  onExit: () => void;
}) {
  return <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center p-6" data-id="element-6981">
      <div className="max-w-lg w-full p-12 text-center bg-white rounded-[2.5rem] shadow-2xl shadow-rose-100 border-2 border-rose-100" data-id="element-6982">
        <div className="text-6xl mb-6" data-id="element-6983">🤔</div>
        <h2 className="text-3xl font-black text-slate-900 mb-4 tracking-tight" data-id="element-6984">
          Er ging iets mis
        </h2>
        <p className="text-slate-600 mb-8 text-lg leading-relaxed font-medium" data-id="element-6985">
          We konden de vraag niet laden. Probeer het nog eens!
        </p>
        <Button onClick={onExit} variant="primary" size="xxl" fullWidth className="shadow-2xl shadow-indigo-200" data-id="element-6986">
          Terug naar overzicht
        </Button>
      </div>
    </div>;
}
// ── Exit Confirmation Modal (assessment mode) ────────────────
export function ExitConfirmModal({
  show,
  currentIndex,
  totalQuestions,
  onContinue,
  onStop
}: {
  show: boolean;
  currentIndex: number;
  totalQuestions: number;
  onContinue: () => void;
  onStop: () => void;
}) {
  return <AnimatePresence data-id="element-6987">
      {show && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50" onClick={onContinue} data-id="element-6988" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.9,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.9,
        y: 20
      }} transition={{
        type: 'spring',
        bounce: 0.2,
        duration: 0.4
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-6989">
            <Card className="max-w-md w-full p-8 shadow-2xl border-2 border-amber-100 bg-white rounded-3xl" data-id="element-6990">
              <div className="text-center" data-id="element-6991">
                <div className="text-5xl mb-4" data-id="element-6992">🤔</div>
                <h2 className="text-2xl font-black text-slate-900 mb-3 tracking-tight" data-id="element-6993">
                  Toets stoppen?
                </h2>
                <p className="text-slate-600 font-medium mb-6 leading-relaxed" data-id="element-6994">
                  Weet je zeker dat je wilt stoppen? Je voortgang wordt
                  opgeslagen als onvolledig.
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-left" data-id="element-6995">
                  <div className="flex items-start gap-3" data-id="element-6996">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-6997" />
                    <p className="text-sm text-amber-800 font-medium" data-id="element-6998">
                      Je hebt {currentIndex + 1} van de {totalQuestions} vragen
                      beantwoord. Als je stopt, kun je later opnieuw beginnen.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3" data-id="element-6999">
                  <Button onClick={onContinue} variant="primary" size="lg" fullWidth data-id="element-7000">
                    Doorgaan met toets
                  </Button>
                  <Button onClick={onStop} variant="secondary" size="lg" fullWidth className="text-rose-600 border-rose-200 hover:bg-rose-50" data-id="element-7001">
                    Stoppen
                  </Button>
                </div>
              </div>
            </Card>
          </motion.div>
        </>}
    </AnimatePresence>;
}